import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Alert,
  Fade,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VideoSettingsIcon from '@mui/icons-material/VideoSettings';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useTheme } from '../../../ThemeContext';
import { GooglePlayIcon } from '../../common/StoreIcons';
import { AI_PLAY_STORE_URL } from '../../../constants/links';

const roles = [
  'Filmmaker / Director',
  'Video Editor / Post-Production',
  'Actor / Talent',
  'Producer / Production House',
  'Screenwriter / Storyteller',
  'Content Creator / Influencer',
  'Other Film Professional',
];

const BetaWaitlistModal = ({ open, onClose, defaultProduct = 'Filmhook Editor', onOpenAiStudio }) => {
  const { isDarkMode } = useTheme();
  const [product, setProduct] = useState(defaultProduct);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(roles[0]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (defaultProduct) {
      setProduct(defaultProduct);
    }
    setSubmitted(false);
  }, [defaultProduct, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !fullName) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 700);
  };

  const handleModalClose = () => {
    setSubmitted(false);
    onClose();
  };

  const isEditor = product === 'Filmhook Editor';
  const themeAccent = isEditor ? '#8b5cf6' : '#06b6d4';

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          background: isDarkMode
            ? 'linear-gradient(135deg, #0d1527 0%, #060b18 100%)'
            : '#ffffff',
          color: isDarkMode ? '#ffffff' : '#0f172a',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(7, 164, 245, 0.2)',
          boxShadow: isDarkMode
            ? '0 24px 60px rgba(0,0,0,0.8)'
            : '0 24px 60px rgba(7,164,245,0.2)',
          overflow: 'hidden',
          position: 'relative',
        },
      }}
    >
      {/* Decorative top accent glow */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: isEditor
            ? 'linear-gradient(90deg, #8b5cf6, #ec4899)'
            : 'linear-gradient(90deg, #06b6d4, #3b82f6)',
        }}
      />

      <DialogTitle sx={{ m: 0, p: 3, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              background: isEditor
                ? 'rgba(139, 92, 246, 0.15)'
                : 'rgba(6, 182, 212, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: themeAccent,
            }}
          >
            {isEditor ? <VideoSettingsIcon /> : <AutoAwesomeIcon />}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a', lineHeight: 1.2 }}>
              Get Early Access
            </Typography>
            <Typography variant="caption" sx={{ color: themeAccent, fontWeight: 700 }}>
              {product} • VIP Waitlist
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleModalClose} sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 1.5 }}>
        {submitted ? (
          <Fade in={submitted}>
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 68,
                  height: 68,
                  borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.15)',
                  color: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2.5,
                }}
              >
                <CheckCircleRoundedIcon sx={{ fontSize: 42 }} />
              </Box>
              <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: isDarkMode ? '#fff' : '#0f172a' }}>
                You're On The Waitlist!
              </Typography>
              <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mb: 3, maxWidth: 380, mx: 'auto' }}>
                Thank you, <strong>{fullName}</strong>. We have reserved your spot for early access to <strong>{product}</strong>. We'll send your invite to <strong>{email}</strong> soon!
              </Typography>
              <Button
                variant="contained"
                onClick={handleModalClose}
                sx={{
                  borderRadius: '25px',
                  px: 4,
                  py: 1,
                  background: 'linear-gradient(90deg, #07a4f5 0%, #5dade2 100%)',
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Done
              </Button>
            </Box>
          </Fade>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mb: 3, lineHeight: 1.6 }}>
              Be among the first creators and filmmakers to test {product}. Get direct access to beta builds, custom LUTs, and exclusive creator perks.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                select
                label="Product of Interest"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    color: isDarkMode ? '#fff' : '#0f172a',
                    '& fieldset': { borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : '#cbd5e1' },
                  },
                  '& .MuiInputLabel-root': { color: isDarkMode ? '#94a3b8' : '#64748b' },
                }}
              >
                <MenuItem value="Filmhook Editor">Filmhook Editor (Cloud Video & Color Suite)</MenuItem>
                <MenuItem value="Filmhook AI">Filmhook AI (Script, Storyboard & Casting Intelligence)</MenuItem>
              </TextField>

              {product === 'Filmhook AI' && (
                <Box
                  sx={{
                    p: 2.2,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.18) 0%, rgba(59, 130, 246, 0.18) 100%)',
                    border: '1.5px solid rgba(6, 182, 212, 0.45)',
                    textAlign: 'center',
                    boxShadow: '0 8px 24px rgba(6, 182, 212, 0.15)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    <AutoAwesomeIcon sx={{ color: '#06b6d4', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0e7490' }}>
                      Interactive Filmhook AI Studio is Available!
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', color: isDarkMode ? '#cbd5e1' : '#475569', mb: 1.8, lineHeight: 1.5 }}>
                    Skip the waitlist form. Test real AI screenplay generation, multi-camera storyboard angles, and talent casting compatibility immediately.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      handleModalClose();
                      if (onOpenAiStudio) onOpenAiStudio();
                    }}
                    startIcon={<AutoAwesomeIcon />}
                    sx={{
                      borderRadius: '14px',
                      fontWeight: 800,
                      background: 'linear-gradient(90deg, #06b6d4 0%, #3b82f6 100%)',
                      textTransform: 'none',
                      py: 1.2,
                      fontSize: '0.9rem',
                      boxShadow: '0 6px 20px rgba(6, 182, 212, 0.4)',
                      mb: 1.2,
                      '&:hover': {
                        background: 'linear-gradient(90deg, #0891b2 0%, #2563eb 100%)',
                      },
                    }}
                  >
                    Launch Interactive AI Studio Now
                  </Button>

                  <Button
                    component="a"
                    href={AI_PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    variant="outlined"
                    startIcon={<GooglePlayIcon size={18} />}
                    sx={{
                      borderRadius: '14px',
                      fontWeight: 800,
                      textTransform: 'none',
                      color: isDarkMode ? '#38bdf8' : '#0284c7',
                      borderColor: 'rgba(6, 182, 212, 0.4)',
                      py: 1,
                      fontSize: '0.85rem',
                      '&:hover': {
                        bgcolor: 'rgba(6, 182, 212, 0.1)',
                        borderColor: '#06b6d4',
                      },
                    }}
                  >
                    Get Filmhook AI on Google Play
                  </Button>
                </Box>
              )}

              <TextField
                label="Full Name"
                placeholder="e.g. Christopher Nolan"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    color: isDarkMode ? '#fff' : '#0f172a',
                    '& fieldset': { borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : '#cbd5e1' },
                  },
                  '& .MuiInputLabel-root': { color: isDarkMode ? '#94a3b8' : '#64748b' },
                }}
              />

              <TextField
                label="Work Email Address"
                placeholder="you@productionstudio.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    color: isDarkMode ? '#fff' : '#0f172a',
                    '& fieldset': { borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : '#cbd5e1' },
                  },
                  '& .MuiInputLabel-root': { color: isDarkMode ? '#94a3b8' : '#64748b' },
                }}
              />

              <TextField
                select
                label="Your Primary Role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    color: isDarkMode ? '#fff' : '#0f172a',
                    '& fieldset': { borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : '#cbd5e1' },
                  },
                  '& .MuiInputLabel-root': { color: isDarkMode ? '#94a3b8' : '#64748b' },
                }}
              >
                {roles.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </TextField>

              <Alert
                severity="info"
                icon={<RocketLaunchRoundedIcon fontSize="inherit" />}
                sx={{
                  borderRadius: '12px',
                  backgroundColor: isDarkMode ? 'rgba(7, 164, 245, 0.1)' : '#f0f9ff',
                  color: isDarkMode ? '#7dd3fc' : '#0369a1',
                  border: isDarkMode ? '1px solid rgba(7, 164, 245, 0.2)' : '1px solid #bae6fd',
                  fontSize: '0.8rem',
                }}
              >
                Spots are prioritized for active filmmakers, editors, and industry professionals.
              </Alert>

              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 1,
                  py: 1.3,
                  borderRadius: '14px',
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  background: isEditor
                    ? 'linear-gradient(90deg, #8b5cf6 0%, #ec4899 100%)'
                    : 'linear-gradient(90deg, #06b6d4 0%, #3b82f6 100%)',
                  boxShadow: isEditor
                    ? '0 8px 24px rgba(139, 92, 246, 0.4)'
                    : '0 8px 24px rgba(6, 182, 212, 0.4)',
                  '&:hover': {
                    opacity: 0.95,
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {loading ? 'Securing Your Spot...' : `Request Early Access to ${product}`}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BetaWaitlistModal;
